import {
  botNameEndpointApiChatBotNameGet,
  createSseClient,
  initializeConversationApiChatInitializeConversationGet,
  webappVersionEndpointApiVersionGet,
  type ChatbotState,
  type UsedSource,
} from "@noa/api-core";
import { getSseRequestConfig, reportError } from "./typed-client";

// ---------------------------------------------------------------------------
// The store-agnostic seam
// ---------------------------------------------------------------------------

/**
 * Every side effect a chat stream can produce, as a callback. This package knows the wire
 * protocol; it does not know where the data goes — `@noa/client-store` supplies an
 * implementation that writes to the zustand stores (see its `chat-runtime.ts`).
 *
 * Deliberately excluded: deciding whether a chunk starts a new assistant bubble or appends to
 * the current one, and mapping a `ChatbotState` to user-facing status text. Both need store
 * state or translations, so they belong on the consumer's side of this interface.
 */
export interface ChatStreamHandlers {
  onChunk: (chunk: string) => void;
  onReasoningChunk: (chunk: string) => void;
  onState: (state: ChatbotState) => void;
  onUsedSource: (source: UsedSource) => void;
  onMessageId: (messageId: string) => void;
  /** The conversation's generated title. Fires at most once, and only on a first user message. */
  onConversationTitle: (title: string) => void;
  onActionDescriptionStart: () => void;
  onActionDescriptionChunk: (chunk: string) => void;
  onActionDescriptionEnd: () => void;
}

export interface ChatbotMetadata {
  botName: string;
  version: string;
}

// ---------------------------------------------------------------------------
// Bot metadata
// ---------------------------------------------------------------------------

const fetchBotName = async (): Promise<string> => {
  const { data } = await botNameEndpointApiChatBotNameGet({ throwOnError: true });
  return data;
};

const fetchVersion = async (): Promise<string> => {
  const { data } = await webappVersionEndpointApiVersionGet({ throwOnError: true });
  return data;
};

/** Fetches bot name + webapp version together. Callers decide what to do with them. */
export const fetchChatbotMetadata = async (): Promise<ChatbotMetadata> => {
  const [botName, version] = await Promise.all([fetchBotName(), fetchVersion()]);

  return { botName, version };
};

// ---------------------------------------------------------------------------
// Conversation initialisation
// ---------------------------------------------------------------------------

/** Creates a server-side conversation and returns its session id. */
export const createConversationSession = async (): Promise<string> => {
  const { data } = await initializeConversationApiChatInitializeConversationGet({
    throwOnError: true,
  });
  return data;
};

// ---------------------------------------------------------------------------
// SSE stream processing
// ---------------------------------------------------------------------------

interface ProcessSSEStreamArgs {
  path: string;
  handlers: ChatStreamHandlers;
  payload?: string;
}

const processSSEStream = async ({ path, handlers, payload }: ProcessSSEStreamArgs): Promise<void> => {
  const { baseUrl, fetch, headers } = await getSseRequestConfig();
  const url = `${baseUrl}${path}`;

  return new Promise((resolve, reject) => {
    const { stream } = createSseClient({
      url,
      method: "POST",
      headers,
      serializedBody: payload,
      fetch,
      sseMaxRetryAttempts: 1,
      onSseEvent: ({ event, data }) => {
        switch (event) {
          case "conversational_response_chunk":
            handlers.onChunk(data as string);
            break;
          case "conversational_reasoning_chunk":
            handlers.onReasoningChunk(data as string);
            break;
          case "state":
            handlers.onState(data as ChatbotState);
            break;
          case "used_source":
            handlers.onUsedSource(data as UsedSource);
            break;
          case "message_id":
            handlers.onMessageId(data as string);
            break;
          case "conversation_title":
            handlers.onConversationTitle(data as string);
            break;
          case "action_description_start":
            handlers.onActionDescriptionStart();
            break;
          case "action_description_chunk":
            handlers.onActionDescriptionChunk(data as string);
            break;
          case "action_description_end":
            handlers.onActionDescriptionEnd();
            break;
          case "done":
            resolve();
            break;
          case "error": {
            const err = new Error(String(data));
            reportError(err);
            reject(err);
            break;
          }
          default:
            break;
        }
      },
      onSseError: (error) => {
        reportError(error);
        reject(error);
      },
    });

    // Consume the generator to drive the stream; resolve on normal completion.
    const consume = async () => {
      // createSseClient's onSseEvent fires for each event; the generator yields
      // only non-empty data lines. We drain it to keep the stream alive.
      for await (const _chunk of stream) {
        // events handled via onSseEvent above
      }
      resolve(); // normal completion (no explicit "done" event)
    };
    consume().catch((err: unknown) => {
      reportError(err);
      reject(err);
    });
  });
};

// ---------------------------------------------------------------------------
// Public API — one stream per entry point, all effects via `handlers`
// ---------------------------------------------------------------------------

/** Streams the assistant's opening greeting for a freshly created session. */
export const streamGreeting = async (sessionId: string, handlers: ChatStreamHandlers): Promise<void> =>
  processSSEStream({ path: `/api/chat/get-greetings/${sessionId}`, handlers });

/** Streams the assistant's reply to a user message. */
export const sendMessage = async (sessionId: string, message: string, handlers: ChatStreamHandlers): Promise<void> =>
  processSSEStream({
    path: `/api/chat/message/${sessionId}`,
    handlers,
    payload: JSON.stringify({ message }),
  });
